import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';
import { formatPrice, calculateWalletBonus, WalletBonusTier } from '../../utils/bookingUtils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bonusTiers: WalletBonusTier[];
}

// Preset top-up amounts
const PRESET_AMOUNTS = [500, 1000, 2000, 3000, 4000, 5000];

// Inner payment form component - content only (buttons rendered separately)
const TopUpPaymentFormContent: React.FC<{
  amount: number;
  bonus: { bonusPercent: number; bonusAmount: number; totalCredit: number };
  error: string | null;
}> = ({ amount, bonus, error }) => {
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between mb-2">
          <span className="text-gray-700">Top-up Amount:</span>
          <span className="font-medium">${formatPrice(amount)}</span>
        </div>
        {bonus.bonusAmount > 0 && (
          <div className="flex justify-between mb-2 text-green-600">
            <span>Bonus:</span>
            <span>+${formatPrice(bonus.bonusAmount)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-blue-200">
          <span className="font-medium text-gray-900">Total Credit:</span>
          <span className="font-bold text-blue-600">${formatPrice(bonus.totalCredit)}</span>
        </div>
      </div>

      {/* Payment Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Details
        </label>
        <PaymentElement />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

// Inner payment form buttons - rendered in fixed footer
const TopUpPaymentFormButtons: React.FC<{
  amount: number;
  isProcessing: boolean;
  onBack: () => void;
}> = ({ amount, isProcessing, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onBack}
        disabled={isProcessing}
        className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        Back
      </button>
      <button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
      >
        {isProcessing ? (
          <>
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
            Processing...
          </>
        ) : (
          `Pay $${formatPrice(amount)}`
        )}
      </button>
    </div>
  );
};

// Wrapper component that handles form submission
const TopUpPaymentForm: React.FC<{
  amount: number;
  bonus: { bonusPercent: number; bonusAmount: number; totalCredit: number };
  onSuccess: () => void;
  onBack: () => void;
}> = ({ amount, bonus, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm payment
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm the top-up on the backend
        const { data: { session } } = await supabase.auth.getSession();

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CONFIRM_WALLET_TOPUP),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              payment_intent_id: paymentIntent.id,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to confirm top-up');
        }

        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <TopUpPaymentFormContent amount={amount} bonus={bonus} error={error} />
      </div>

      {/* Fixed footer with buttons - includes safe area padding */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <TopUpPaymentFormButtons amount={amount} isProcessing={isProcessing} onBack={onBack} />
      </div>
    </form>
  );
};

type PaymentMethod = 'card' | 'terminal';
type Step = 'amount' | 'method' | 'payment' | 'terminal' | 'success';

const TopUpModal: React.FC<TopUpModalProps> = ({ isOpen, onClose, onSuccess, bonusTiers }) => {
  const [step, setStep] = useState<Step>('amount');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topUpResult, setTopUpResult] = useState<{
    topupAmount: number;
    bonusAmount: number;
    totalCredit: number;
    newBalance: number;
  } | null>(null);

  // Payment method selection
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');

  // Terminal states
  const [isProcessingTerminal, setIsProcessingTerminal] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState<string>('');
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [canCancelPayment, setCanCancelPayment] = useState(false);
  const terminalRef = useRef<any>(null);
  const discoveredReadersRef = useRef<any[]>([]);
  const paymentIntentRef = useRef<any>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('amount');
      setSelectedAmount(null);
      setIsCustomAmount(false);
      setCustomAmountInput('');
      setClientSecret(null);
      setError(null);
      setTopUpResult(null);
      setPaymentMethod('card');
      setIsProcessingTerminal(false);
      setTerminalStatus('');
      setTerminalError(null);
      setCanCancelPayment(false);
    }
  }, [isOpen]);

  // Initialize Stripe Terminal
  useEffect(() => {
    const initializeTerminal = async () => {
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
        console.error('Stripe publishable key not found');
        return;
      }

      try {
        const StripeTerminal = await loadStripeTerminal();

        if (!StripeTerminal) {
          console.error('Failed to load Stripe Terminal');
          return;
        }

        terminalRef.current = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CONNECTION_TOKEN), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token || ''}`,
                'apikey': SUPABASE_ANON_KEY,
              },
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || 'Failed to fetch connection token');
            }

            return data.secret;
          },
          onUnexpectedReaderDisconnect: () => {
            setTerminalStatus('Reader disconnected');
            setTerminalError('Reader disconnected unexpectedly');
          },
        });
      } catch (error) {
        console.error('Terminal initialization failed:', error);
      }
    };

    if (isOpen) {
      initializeTerminal();
    }

    return () => {
      // Cleanup: safely disconnect reader when modal closes
      const cleanup = async () => {
        if (terminalRef.current) {
          try {
            const status = terminalRef.current.getConnectionStatus();
            if (status === 'connected') {
              await terminalRef.current.disconnectReader();
            }
          } catch (err) {
            // Silent cleanup
          }
        }
      };
      cleanup();
    };
  }, [isOpen]);

  const getFinalAmount = (): number => {
    if (isCustomAmount && customAmountInput) {
      return parseFloat(customAmountInput) || 0;
    }
    return selectedAmount || 0;
  };

  const amount = getFinalAmount();
  const bonus = isCustomAmount
    ? { bonusPercent: 0, bonusAmount: 0, totalCredit: amount }
    : calculateWalletBonus(amount, bonusTiers);

  const handleContinueToMethod = () => {
    if (amount <= 0) {
      setError('Please select a top-up amount');
      return;
    }
    setError(null);
    setStep('method');
  };

  const handleContinueToPayment = async () => {
    if (amount <= 0) {
      setError('Please select a top-up amount');
      return;
    }

    setIsCreatingIntent(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Check if user is authenticated
      if (!session?.access_token) {
        throw new Error('Please log in to add funds to your wallet');
      }

      const response = await fetch(
        edgeFunctionUrl(EdgeFunctions.CREATE_WALLET_TOPUP),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ amount, skip_bonus: isCustomAmount }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create top-up');
      }

      const data = await response.json();
      setClientSecret(data.clientSecret);
      setStep('payment');
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsCreatingIntent(false);
    }
  };

  // Handle cancel terminal payment
  const handleCancelTerminalPayment = async () => {
    try {
      setTerminalStatus('Cancelling...');

      // Cancel the payment collection if in progress
      if (paymentIntentRef.current) {
        await terminalRef.current?.cancelCollectPaymentMethod();
      }

      // Disconnect reader
      if (terminalRef.current) {
        const status = terminalRef.current.getConnectionStatus();
        if (status === 'connected') {
          await terminalRef.current.disconnectReader();
        }
      }

      // Reset states and go back to method selection
      setTerminalStatus('');
      setTerminalError(null);
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;
      setStep('method');
    } catch (error: any) {
      setTerminalError('Failed to cancel payment. Please try closing the modal.');
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
    }
  };

  // Handle terminal payment
  const handleTerminalPayment = async () => {
    if (amount <= 0) {
      setError('Please select a top-up amount');
      return;
    }

    setIsProcessingTerminal(true);
    setTerminalError(null);
    setTerminalStatus('Connecting to reader...');
    setCanCancelPayment(false);
    setStep('terminal');

    try {
      if (!terminalRef.current) {
        throw new Error('Terminal not initialized');
      }

      // Step 1: Discover readers
      setTerminalStatus('Discovering readers...');

      const isTestMode = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.includes('test') || false;
      const discoverConfig = isTestMode
        ? { simulated: true }
        : { location: process.env.REACT_APP_STRIPE_LOCATION_ID };

      if (!isTestMode && !process.env.REACT_APP_STRIPE_LOCATION_ID) {
        throw new Error('Stripe Location ID not configured');
      }

      const discoverResult = await terminalRef.current.discoverReaders(discoverConfig);

      if (discoverResult.error) {
        throw new Error(`Failed to discover readers: ${discoverResult.error.message}`);
      }

      if (discoverResult.discoveredReaders.length === 0) {
        const errorMsg = isTestMode
          ? 'No simulated readers found. Try refreshing the page.'
          : 'No readers found. Make sure your S700 is online and registered.';
        throw new Error(errorMsg);
      }

      discoveredReadersRef.current = discoverResult.discoveredReaders;

      // Step 2: Connect to first available reader
      const readerLabel = discoveredReadersRef.current[0].label || 'reader';
      const readerType = isTestMode ? ' (simulated)' : '';
      setTerminalStatus(`Connecting to ${readerLabel}${readerType}...`);
      const connectResult = await terminalRef.current.connectReader(discoveredReadersRef.current[0]);

      if (connectResult.error) {
        throw new Error(`Failed to connect to reader: ${connectResult.error.message}`);
      }

      // Step 3: Create Terminal Wallet TopUp PaymentIntent via edge function
      setTerminalStatus('Creating payment...');
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Please log in to add funds to your wallet');
      }

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TERMINAL_WALLET_TOPUP), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ amount, skip_bonus: isCustomAmount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment');
      }

      paymentIntentRef.current = data.clientSecret;

      // Step 4: Collect payment on reader
      setTerminalStatus('Present card on reader...');
      setCanCancelPayment(true);

      const collectResult = await terminalRef.current.collectPaymentMethod(data.clientSecret);

      if (collectResult.error) {
        if (collectResult.error.code === 'canceled') {
          throw new Error('Payment cancelled');
        }
        throw new Error(`Payment collection failed: ${collectResult.error.message}`);
      }

      setCanCancelPayment(false);

      // Step 5: Process payment
      setTerminalStatus('Processing payment...');
      const processResult = await terminalRef.current.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        throw new Error(`Payment processing failed: ${processResult.error.message}`);
      }

      // Clear the reader display so S700 returns to idle screen
      try {
        await terminalRef.current.clearReaderDisplay();
      } catch (clearErr) {
        // Non-blocking: payment already succeeded
      }

      // Step 6: Confirm wallet top-up on backend
      setTerminalStatus('Confirming top-up...');
      const confirmResponse = await fetch(edgeFunctionUrl(EdgeFunctions.CONFIRM_WALLET_TOPUP), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          payment_intent_id: processResult.paymentIntent.id,
        }),
      });

      const confirmData = await confirmResponse.json();

      if (!confirmResponse.ok) {
        console.error('Failed to confirm top-up:', confirmData.error);
        // Don't throw - payment was successful on terminal
      }

      // Disconnect reader
      try {
        if (terminalRef.current) {
          const status = terminalRef.current.getConnectionStatus();
          if (status === 'connected') {
            await terminalRef.current.disconnectReader();
          }
        }
      } catch (disconnectErr) {
        // Silent cleanup
      }

      // Show success
      handlePaymentSuccess();

    } catch (error: any) {
      console.error('Terminal payment error:', error);
      setTerminalError(error.message || 'Failed to process payment');
      setTerminalStatus('');

      // Disconnect reader on error
      try {
        if (terminalRef.current) {
          const status = terminalRef.current.getConnectionStatus();
          if (status === 'connected') {
            await terminalRef.current.disconnectReader();
          }
        }
      } catch (disconnectErr) {
        // Silent cleanup
      }
    } finally {
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;
    }
  };

  const handlePaymentSuccess = () => {
    setTopUpResult({
      topupAmount: amount,
      bonusAmount: bonus.bonusAmount,
      totalCredit: bonus.totalCredit,
      newBalance: 0, // Will be updated when modal closes
    });
    setStep('success');
  };

  const handleClose = () => {
    if (step === 'success') {
      onSuccess();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'success' ? 'Top-Up Successful!' : 'Add Funds to Wallet'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step 1: Select Amount - with scrollable content and fixed footer */}
        {step === 'amount' && (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Select an amount to add to your wallet. Get bonus credits on larger top-ups!
                </p>

                {/* Preset Amounts */}
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_AMOUNTS.map((preset) => {
                    const presetBonus = calculateWalletBonus(preset, bonusTiers);
                    return (
                      <button
                        key={preset}
                        onClick={() => {
                          setSelectedAmount(preset);
                          setIsCustomAmount(false);
                          setCustomAmountInput('');
                        }}
                        className={`p-3 rounded-lg border-2 text-center transition-colors ${
                          selectedAmount === preset && !isCustomAmount
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium text-gray-900">${preset.toLocaleString()}</span>
                        {presetBonus.bonusAmount > 0 && (
                          <span className="block text-xs text-green-600">
                            +${formatPrice(presetBonus.bonusAmount)} bonus
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-gray-500 text-sm">Or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Custom Amount Section */}
                <div
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    isCustomAmount
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium text-gray-900 mb-2">Custom Amount (No Bonus)</div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="Enter amount"
                      value={customAmountInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Validate: allow empty, or positive numbers with up to 2 decimal places
                        if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                          setCustomAmountInput(value);
                          setIsCustomAmount(true);
                          setSelectedAmount(null);
                        }
                      }}
                      onFocus={() => {
                        setIsCustomAmount(true);
                        setSelectedAmount(null);
                      }}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Enter any amount (min $1)</p>
                </div>

                <p className="text-xs text-gray-500">
                  Wallet top-up balance is non-refundable and cannot be redeemed for cash.
                </p>

                {/* Preview */}
                {amount > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">You pay:</span>
                      <span className="font-medium">${formatPrice(amount)}</span>
                    </div>
                    {bonus.bonusAmount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Bonus:</span>
                        <span>+${formatPrice(bonus.bonusAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200 mt-2">
                      <span>You receive:</span>
                      <span className="text-blue-600">${formatPrice(bonus.totalCredit)}</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={handleContinueToMethod}
                disabled={amount <= 0}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                Continue to Payment
              </button>
            </div>
          </>
        )}

        {/* Step 2: Payment Method Selection */}
        {step === 'method' && (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Top-up Amount:</span>
                    <span className="font-medium">${formatPrice(amount)}</span>
                  </div>
                  {bonus.bonusAmount > 0 && (
                    <div className="flex justify-between mb-2 text-green-600">
                      <span>Bonus:</span>
                      <span>+${formatPrice(bonus.bonusAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="font-medium text-gray-900">Total Credit:</span>
                    <span className="font-bold text-blue-600">${formatPrice(bonus.totalCredit)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Payment Method
                  </label>
                  <div className="space-y-2">
                    {/* Enter Card Details Option */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                        paymentMethod === 'card'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl">💳</span>
                      <div>
                        <div className="font-medium text-gray-900">Enter Card Details</div>
                        <div className="text-sm text-gray-500">Pay with debit or credit card</div>
                      </div>
                    </button>

                    {/* Pay at Terminal Option */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('terminal')}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                        paymentMethod === 'terminal'
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl">📱</span>
                      <div>
                        <div className="font-medium text-gray-900">Pay at Terminal</div>
                        <div className="text-sm text-gray-500">Tap or insert card on reader</div>
                      </div>
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep('amount')}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={paymentMethod === 'terminal' ? handleTerminalPayment : handleContinueToPayment}
                  disabled={isCreatingIntent}
                  className={`flex-1 py-3 px-4 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                    paymentMethod === 'terminal'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isCreatingIntent ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      Processing...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Terminal Payment */}
        {step === 'terminal' && (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Top-up Amount:</span>
                    <span className="font-medium">${formatPrice(amount)}</span>
                  </div>
                  {bonus.bonusAmount > 0 && (
                    <div className="flex justify-between mb-2 text-green-600">
                      <span>Bonus:</span>
                      <span>+${formatPrice(bonus.bonusAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="font-medium text-gray-900">Total Credit:</span>
                    <span className="font-bold text-blue-600">${formatPrice(bonus.totalCredit)}</span>
                  </div>
                </div>

                {/* Terminal Status */}
                {terminalStatus && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      {isProcessingTerminal && (
                        <span className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                      )}
                      <span className="text-blue-800 font-medium">{terminalStatus}</span>
                    </div>
                  </div>
                )}

                {/* Terminal Error */}
                {terminalError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{terminalError}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              {canCancelPayment ? (
                <button
                  onClick={handleCancelTerminalPayment}
                  className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel Payment
                </button>
              ) : terminalError ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setTerminalError(null);
                      setStep('method');
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleTerminalPayment}
                    className="flex-1 py-3 px-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <button
                  disabled
                  className="w-full py-3 bg-gray-100 text-gray-400 rounded-lg font-medium cursor-not-allowed"
                >
                  Processing...
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 4: Card Payment - wrapped in flex container for proper height */}
        {step === 'payment' && clientSecret && (
          <div className="flex-1 flex flex-col min-h-0 [&>*]:flex-1 [&>*]:flex [&>*]:flex-col [&>*]:min-h-0">
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2563eb',
                  },
                },
              }}
            >
              <TopUpPaymentForm
                amount={amount}
                bonus={bonus}
                onSuccess={handlePaymentSuccess}
                onBack={() => {
                  setStep('method');
                  setClientSecret(null);
                }}
              />
            </Elements>
          </div>
        )}

        {/* Step 3: Success - with scrollable content and fixed footer */}
        {step === 'success' && (
          <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Funds Added Successfully!
                </h3>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800">
                    <span className="font-medium">${formatPrice(bonus.totalCredit)}</span> has been added to your wallet
                  </p>
                  {bonus.bonusAmount > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Includes ${formatPrice(bonus.bonusAmount)} bonus!
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed Footer with safe area padding */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TopUpModal;
